import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppointmentCancelRequest,
  AppointmentCompleteRequest,
  AppointmentCreateRequest,
  AppointmentItem,
  AppointmentUpdateRequest,
  AppointmentWarning,
  OverdueCount,
} from '@crm/shared';
import { api } from '../../lib/api';

export type AppointmentListFilter = {
  from: string; // YYYY-MM-DD
  to: string;
  employeeId?: number;
};

export const appointmentsKeys = {
  all: ['appointments'] as const,
  list: (f: AppointmentListFilter) => ['appointments', 'list', f] as const,
  one: (id: number) => ['appointments', 'one', id] as const,
  overdue: (employeeId?: number) =>
    ['appointments', 'overdue', employeeId ?? 'all'] as const,
  overdueCount: ['appointments', 'overdue', 'count'] as const,
};

export function useAppointmentsList(filter: AppointmentListFilter, enabled = true) {
  return useQuery({
    queryKey: appointmentsKeys.list(filter),
    enabled,
    queryFn: async () => {
      const params: Record<string, string> = {
        from: filter.from,
        to: filter.to,
      };
      if (filter.employeeId !== undefined) params.employeeId = String(filter.employeeId);
      const res = await api.get<AppointmentItem[]>('/appointments', { params });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useAppointmentCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AppointmentCreateRequest) => {
      const res = await api.post<AppointmentItem>('/appointments', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: appointmentsKeys.all }),
  });
}

export function useAppointmentUpdate(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AppointmentUpdateRequest) => {
      const res = await api.patch<AppointmentItem>(`/appointments/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: appointmentsKeys.all }),
  });
}

export function useAppointmentCancel(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AppointmentCancelRequest) => {
      const res = await api.post<AppointmentItem>(`/appointments/${id}/cancel`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: appointmentsKeys.all }),
  });
}

export function useAppointmentComplete(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input?: AppointmentCompleteRequest) => {
      const res = await api.post<AppointmentItem>(
        `/appointments/${id}/complete`,
        input ?? {},
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: appointmentsKeys.all }),
  });
}

/**
 * Faza 19 — cofnięcie zatwierdzonej wizyty (COMPLETED → CONFIRMED).
 * Backend pilnuje RBAC i okna 24h. Inwaliduje też klucze lojalności,
 * bo decrement visitsCount mogł zmienić tier klienta.
 */
export function useAppointmentUncomplete(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<AppointmentItem>(`/appointments/${id}/uncomplete`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appointmentsKeys.all });
      qc.invalidateQueries({ queryKey: ['loyalty'] });
    },
  });
}

export function useAppointmentNoShow(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<AppointmentItem>(`/appointments/${id}/no-show`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: appointmentsKeys.all }),
  });
}

/**
 * Faza 19 — lista zaległych wizyt (BOOKED/CONFIRMED z `startAt < now`).
 * Backend filtruje per rola: EMPLOYEE widzi tylko swoje, RECEPTION/ADMIN
 * wszystkie (opcjonalny filtr `employeeId`).
 */
export function useOverdueAppointments(employeeId?: number) {
  return useQuery({
    queryKey: appointmentsKeys.overdue(employeeId),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (employeeId !== undefined) params.employeeId = String(employeeId);
      const res = await api.get<AppointmentItem[]>('/appointments/overdue', {
        params,
      });
      return res.data;
    },
    staleTime: 30_000,
  });
}

/**
 * Faza 19 — lekki licznik dla badge w sidebarze. Polling 60 s
 * (analogicznie do dzwoneczka powiadomień z F10). Tańszy niż pobieranie
 * pełnej listy.
 */
export function useOverdueCount() {
  return useQuery({
    queryKey: appointmentsKeys.overdueCount,
    queryFn: async () => {
      const res = await api.get<OverdueCount>('/appointments/overdue/count');
      return res.data;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/**
 * Faza 19 — wyciąga details z błędów 409 zwracanych przez complete()
 * (OVERDUE_COMPLETION) i uncomplete() (UNCOMPLETE_OUT_OF_WINDOW). Frontend
 * używa do renderowania właściwego komunikatu (np. „Wizyta sprzed X dni —
 * tylko admin może zatwierdzić").
 */
export type CompletionErrorDetails =
  | { code: 'OVERDUE_COMPLETION'; ageInDays: number; threshold: number }
  | { code: 'UNCOMPLETE_OUT_OF_WINDOW'; windowHours: number }
  | null;

export function extractCompletionError(err: unknown): CompletionErrorDetails {
  if (typeof err !== 'object' || err === null) return null;
  const data = (err as { response?: { data?: unknown } }).response?.data;
  if (typeof data !== 'object' || data === null) return null;
  const detail = (data as { error?: { details?: unknown } }).error?.details;
  if (typeof detail !== 'object' || detail === null) return null;
  const d = detail as Record<string, unknown>;
  if (d.code === 'OVERDUE_COMPLETION') {
    return {
      code: 'OVERDUE_COMPLETION',
      ageInDays: typeof d.ageInDays === 'number' ? d.ageInDays : 0,
      threshold: typeof d.threshold === 'number' ? d.threshold : 30,
    };
  }
  if (d.code === 'UNCOMPLETE_OUT_OF_WINDOW') {
    return {
      code: 'UNCOMPLETE_OUT_OF_WINDOW',
      windowHours: typeof d.windowHours === 'number' ? d.windowHours : 24,
    };
  }
  return null;
}

/**
 * Wyciąga z błędu axios payload `details.warnings` (z 409 wracających przy
 * walidacji biznesowej wizyt). Zwraca pustą listę gdy to inny rodzaj błędu.
 */
export function extractWarnings(err: unknown): AppointmentWarning[] {
  if (typeof err !== 'object' || err === null) return [];
  const axiosErr = err as { response?: { data?: unknown } };
  const data = axiosErr.response?.data;
  if (typeof data !== 'object' || data === null) return [];
  const detail = (data as { error?: { details?: unknown } }).error?.details;
  if (typeof detail !== 'object' || detail === null) return [];
  const warnings = (detail as { warnings?: unknown }).warnings;
  if (!Array.isArray(warnings)) return [];
  return warnings as AppointmentWarning[];
}
